import { useEffect, useState } from "react";
import { Loader2, ArrowRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import api from "../../lib/api";
import logger from "../../lib/logger";
import { openWhatsAppEnquiry } from "../../lib/whatsappNotify";

const INITIAL_FORM = {
  name: "",
  email: "",
  phone: "",
  property_postcode: "",
  properties_owned: "",
  message: "",
  send_copy: false,
};

const PROPERTIES_OWNED_OPTIONS = [
  "1",
  "2 – 3",
  "4 – 9",
  "10 – 19",
  "20+",
];

/**
 * ServiceEnquiryDialog — single modal that captures an enquiry for any of the
 * additional landlord services. Submits via /api/contact with a structured
 * message body and source=`service-<id>` so admin can see exactly which
 * service was requested.
 */
export function ServiceEnquiryDialog({ open, onOpenChange, service }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever a new service is opened
  useEffect(() => {
    if (open) setForm(INITIAL_FORM);
  }, [open, service?.title]);

  const handleChange = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const buildMessage = () => {
    const lines = [
      `Subject: ${service?.title} — Request a Quote`,
      "",
      `Service: ${service?.title}`,
      `Indicative price: ${service?.price || "N/A"}`,
      `Property postcode: ${form.property_postcode || "Not provided"}`,
      `Number of properties owned: ${form.properties_owned || "Not provided"}`,
      "",
      "Message:",
      form.message || "(no additional message)",
    ];
    return lines.join("\n");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.property_postcode || !form.properties_owned) {
      toast.error("Please complete all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      await api.submitContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: buildMessage(),
        submission_type: "service_request",
        source: `service-${(service?.id || service?.title || "additional").toString().toLowerCase().replace(/\s+/g, "-")}`,
        send_copy: form.send_copy,
      });

      toast.success(`Request sent for ${service?.title} — we'll be in touch shortly.`);
      onOpenChange(false);

      // Full WhatsApp notification — includes every field + full message body.
      try {
        openWhatsAppEnquiry({
          name: form.name,
          email: form.email,
          phone: form.phone,
          submission_type: "service_request",
          source: `service-${(service?.id || service?.title || "additional").toString().toLowerCase().replace(/\s+/g, "-")}`,
          subject: `Request a Quote — ${service?.title}`,
          reference: `Postcode ${form.property_postcode} · Properties owned: ${form.properties_owned}${service?.price ? ` · Indicative price: ${service.price}` : ""}`,
          body: form.message || "(no additional message provided)",
        });
      } catch (err) {
        logger.warn("WhatsApp redirect failed:", err?.message);
      }
    } catch (err) {
      logger.error("Service enquiry submission failed:", err);
      const status = err?.response?.status;
      if (!err?.response) {
        toast.error("Cannot reach the server. Please try again or call us directly.");
      } else if (status === 401) {
        toast.error("Session error. Please refresh and try again.");
      } else {
        toast.error("Failed to send request. Please try again or call us directly.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) return null;

  const Icon = service.icon;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
        data-testid="service-enquiry-dialog"
      >
        {/* Header */}
        <div className="bg-navy text-white px-6 py-5 relative">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 text-white/60 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-start gap-3 pr-8">
            {Icon && (
              <div className="w-11 h-11 rounded-lg bg-gold/15 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-gold" />
              </div>
            )}
            <div>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-white" data-testid="service-enquiry-title">
                  Request a quote: {service.title}
                </DialogTitle>
                <DialogDescription className="text-white/70 text-sm mt-1">
                  Tell us a few details and we'll come back to you with a tailored quote.
                </DialogDescription>
              </DialogHeader>
              {service.price && (
                <div className="text-gold text-sm font-medium mt-2">
                  Indicative price: {service.price}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-6 space-y-4">
          {/* Read-only service confirmation chip — reassures the user the
              correct service is pre-selected and impossible to change here. */}
          <div className="flex items-center gap-2 rounded-lg bg-gold/10 border border-gold/30 px-3 py-2 text-sm">
            <span className="text-slate-500">Selected service:</span>
            <span className="font-medium text-navy" data-testid="service-enquiry-selected-service">
              {service.title}
            </span>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">Full name *</label>
            <Input
              value={form.name}
              onChange={handleChange("name")}
              placeholder="Full name"
              required
              data-testid="service-enquiry-name"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Email address *</label>
              <Input
                value={form.email}
                onChange={handleChange("email")}
                placeholder="you@example.com"
                type="email"
                required
                data-testid="service-enquiry-email"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Telephone *</label>
              <Input
                value={form.phone}
                onChange={handleChange("phone")}
                placeholder="01924 ..."
                type="tel"
                required
                data-testid="service-enquiry-phone"
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Property postcode *</label>
              <Input
                value={form.property_postcode}
                onChange={handleChange("property_postcode")}
                placeholder="e.g. WF17 5HJ"
                required
                data-testid="service-enquiry-postcode"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy mb-1.5">Properties owned *</label>
              <Select
                value={form.properties_owned}
                onValueChange={(value) => setForm((f) => ({ ...f, properties_owned: value }))}
              >
                <SelectTrigger data-testid="service-enquiry-properties-owned">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTIES_OWNED_OPTIONS.map((opt) => (
                    <SelectItem key={opt} value={opt}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-navy mb-1.5">
              Message <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <Textarea
              value={form.message}
              onChange={handleChange("message")}
              placeholder="Any specifics we should know — access arrangements, urgency, multiple properties..."
              rows={3}
              data-testid="service-enquiry-message"
            />
          </div>

          <div className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2.5">
            <Checkbox
              id="service-send-copy"
              checked={form.send_copy}
              onCheckedChange={(checked) =>
                setForm((f) => ({ ...f, send_copy: checked === true }))
              }
              data-testid="service-enquiry-send-copy"
              className="mt-0.5"
            />
            <label
              htmlFor="service-send-copy"
              className="text-sm text-slate-700 leading-snug cursor-pointer select-none"
            >
              Also send me a copy of this quote request
              <span className="block text-xs text-slate-500 mt-0.5">
                We&apos;ll email you a confirmation with what you submitted.
              </span>
            </label>
          </div>

          <DialogFooter className="pt-2 flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            <p className="text-xs text-slate-500 text-left">
              We typically respond within one business day.
            </p>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-gold hover:bg-gold-light text-navy font-semibold flex-1 sm:flex-none"
                data-testid="service-enquiry-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Request a Quote
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceEnquiryDialog;
